import { useEffect } from 'react';

// Detect if running on preview/staging environment
const isPreviewEnvironment = () => {
  const hostname = window.location.hostname;
  const previewPatterns = [
    '.preview.emergentagent.com',
    '.preview.emergent.sh',
    'localhost',
    '127.0.0.1',
    '.vercel.app',
    '.netlify.app',
    'staging.',
    'preview.',
    'dev.'
  ];
  return previewPatterns.some(pattern => hostname.includes(pattern));
};

const SEOHead = ({ title, description, keywords, canonical, ogImage }) => {
  useEffect(() => {
    // Set document title - remove duplicate brand suffix if already present
    let cleanTitle = title;
    if (cleanTitle && cleanTitle.includes('| ToolsForge')) {
      cleanTitle = cleanTitle.replace(/\s*\|\s*ToolsForge\s*$/i, '');
    }
    document.title = cleanTitle ? `${cleanTitle} | ToolsForge` : 'ToolsForge - AI Tools & Digital Utilities';
    
    // Helper to set or create meta tag
    const setMeta = (name, content, property = false) => {
      const attr = property ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // CRITICAL: Prevent indexing of preview/staging environments
    if (isPreviewEnvironment()) {
      setMeta('robots', 'noindex, nofollow');
    } else {
      // Remove noindex if on production (in case it was set before)
      const robotsMeta = document.querySelector('meta[name="robots"]');
      if (robotsMeta && robotsMeta.getAttribute('content') === 'noindex, nofollow') {
        robotsMeta.remove();
      }
    }

    // Set meta description
    if (description) {
      setMeta('description', description);
      setMeta('og:description', description, true);
      setMeta('twitter:description', description);
    }

    // Set keywords
    if (keywords) {
      setMeta('keywords', keywords);
    }

    // Set Open Graph tags
    setMeta('og:title', title || 'ToolsForge', true);
    setMeta('og:type', 'website', true);
    setMeta('og:site_name', 'ToolsForge', true);
    
    if (canonical) {
      setMeta('og:url', canonical, true);
      
      // Set canonical link
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonical);
    }

    if (ogImage) {
      setMeta('og:image', ogImage, true);
      setMeta('twitter:image', ogImage);
      setMeta('twitter:card', 'summary_large_image');
    }

    // Twitter tags
    setMeta('twitter:title', title || 'ToolsForge');
    setMeta('twitter:site', '@toolsforge');

    // Cleanup function
    return () => {
      document.title = 'ToolsForge - AI Tools & Digital Utilities';
    };
  }, [title, description, keywords, canonical, ogImage]);

  return null;
};

export default SEOHead;
